import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { CalendarBlank, Copy, ArrowsClockwise, X, EnvelopeSimple } from "@phosphor-icons/react";

export default function CalendarSubscriptionCard({ apiBasePath = "/in-house-portal" }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await API.get(`${apiBasePath}/calendar-feed/status`);
      setStatus(data);
    } catch {
      setStatus({ active: false });
    }
  };

  useEffect(() => { load(); }, [apiBasePath]);  // eslint-disable-line react-hooks/exhaustive-deps

  const rotate = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`${apiBasePath}/calendar-feed/rotate`);
      setStatus({ active: true, https_url: data.https_url, webcal_url: data.webcal_url });
      toast.success("Subscription URL ready — paste it into Google/Apple/Outlook.");
    } catch {
      toast.error("Could not generate subscription URL.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this subscription? Any calendar app currently using the URL will stop receiving updates.")) return;
    setBusy(true);
    try {
      await API.delete(`${apiBasePath}/calendar-feed`);
      setStatus({ active: false });
      toast.success("Subscription revoked.");
    } catch {
      toast.error("Could not revoke subscription.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Could not copy.");
    }
  };

  const emailMe = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`${apiBasePath}/calendar-feed/email-me`);
      toast.success(`Sent to ${data.email}. Open it on your phone to one-tap subscribe.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email.");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <div className="border border-border bg-card rounded-lg p-4" data-testid="calendar-subscription-card">
      <div className="flex items-center gap-2 mb-1">
        <CalendarBlank size={18} className="text-slate-500" />
        <h2 className="text-base font-semibold">Live calendar subscription</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Subscribe your calendar app to a live feed of your jobs. Google / Apple / Outlook will refresh on their own schedule (a few hours), so changes show up automatically.
      </p>

      {status.active ? (
        <div className="space-y-3">
          <UrlRow label="webcal:// (one-click on iPhone / Mac)" value={status.webcal_url} testid="webcal-url" onCopy={copy} primary />
          <UrlRow label="https:// (Google / Outlook)" value={status.https_url} testid="https-url" onCopy={copy} />
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <Button size="sm" variant="default" onClick={emailMe} disabled={busy} data-testid="email-feed-url">
              <EnvelopeSimple size={14} className="mr-1" /> Email me this URL
            </Button>
            <Button size="sm" variant="outline" onClick={rotate} disabled={busy} data-testid="rotate-feed-token">
              <ArrowsClockwise size={14} className="mr-1" /> Rotate URL
            </Button>
            <Button size="sm" variant="ghost" onClick={revoke} disabled={busy} className="text-red-600 hover:text-red-700" data-testid="revoke-feed">
              <X size={14} className="mr-1" /> Revoke
            </Button>
          </div>
        </div>
      ) : (
        <Button size="sm" onClick={rotate} disabled={busy} data-testid="generate-feed-url">
          <CalendarBlank size={14} className="mr-1" /> Generate subscription URL
        </Button>
      )}

      <details className="mt-3 text-[11px] text-muted-foreground">
        <summary className="cursor-pointer">How to subscribe</summary>
        <div className="mt-2 space-y-1.5 pl-2">
          <p><strong>iPhone / Mac:</strong> tap the webcal:// link — your Calendar app will offer to subscribe.</p>
          <p><strong>Google Calendar:</strong> click + next to "Other calendars" → "From URL" → paste the https:// URL.</p>
          <p><strong>Outlook:</strong> Calendar → "Add calendar" → "Subscribe from web" → paste the https:// URL.</p>
        </div>
      </details>
    </div>
  );
}

function UrlRow({ label, value, testid, onCopy, primary }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">{label}</p>
      <div className={`flex items-center gap-2 rounded-md border border-border ${primary ? "bg-primary/5" : "bg-muted/40"} px-2 py-1.5`}>
        <code className="text-[11px] truncate flex-1 select-all" data-testid={testid}>{value}</code>
        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onCopy(value)} data-testid={`${testid}-copy`}>
          <Copy size={12} />
        </Button>
      </div>
    </div>
  );
}
